import { useState, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFleetSearchDetails } from "./api";
import { queryKeys } from "../../../../lib/query";

/**
 * Fleet search hook backed by React Query.
 *
 * - Initial (unfiltered) load uses queryKeys.fleet.list({}) which is identical
 *   to FleetDataProvider's queryKey, so the cache is shared — no duplicate call.
 * - Filtered refetches (drill-down, column filters) use
 *   queryKeys.fleet.list(filterParams), creating a separate cache entry per
 *   unique filter combination.
 *
 * Returns the same shape as the old reducer: { state: { isLoading, isError, data }, refetch }
 */
function useFleetSearchReducer() {
  const queryClient = useQueryClient();
  const [filterParams, setFilterParams] = useState({});

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.fleet.list(filterParams),
    queryFn: async () => {
      const result = await fetchFleetSearchDetails(filterParams);
      return result?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const state = useMemo(
    () => ({
      isLoading,
      isError,
      data: data ?? [],
    }),
    [data, isLoading, isError]
  );

  const refetch = useCallback(
    (newFilterParams = {}) => {
      const hasFilters = Object.keys(newFilterParams).length > 0;
      setFilterParams(newFilterParams);
      // If switching back to no-filters, invalidate so we get fresh data
      if (!hasFilters) {
        queryClient.invalidateQueries({ queryKey: queryKeys.fleet.list({}) });
      }
    },
    [queryClient]
  );

  return { state, refetch };
}

export default useFleetSearchReducer;
